import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowLeft,
  Home,
  ZoomIn,
  ZoomOut,
  Maximize,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';
import PVSPageLayout from './PVSPageLayout';
import { getAuthHeaders } from '../utils/authHeaders';
import { getCachedDoc, saveCachedDoc } from '../utils/calorDocCache';

const API_URL = 'https://app.pvslimited.co.uk/api';
import { DEFAULT_PDF_ZOOM } from '../utils/pdfViewerDefaults';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.js';

const isAuthorisedForCalor = () => {
  try {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) return false;
    JSON.parse(userData);
    return true;
  } catch {
    return false;
  }
};

const CalorPDFViewer = ({ title, filename, onBack, onReturnHome }) => {
  const [authorised, setAuthorised] = useState(() => isAuthorisedForCalor());

  const [pdfDoc, setPdfDoc] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [zoom, setZoom] = useState(DEFAULT_PDF_ZOOM);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOfflineCopy, setIsOfflineCopy] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [pageTextCache, setPageTextCache] = useState(new Map());

  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);
  const renderTaskRef = useRef(null);
  const highlightLayerRef = useRef(null);
  const activeSearchTermRef = useRef('');

  const pdfUrl = `${API_URL}/calor/documents/${filename}`;
  const versionUrl = `${API_URL}/calor/documents/${filename}/version`;

  // Hard gate: bounce out immediately if the session isn't valid.
  useEffect(() => {
    const ok = isAuthorisedForCalor();
    if (!ok) {
      setAuthorised(false);
      if (typeof onBack === 'function') onBack();
    }
  }, [onBack]);

  useEffect(() => {
    if (!authorised) return undefined;

    let cancelled = false;

    // Ask the server for the current version, but don't hang forever if the
    // network is flaky/offline — that's exactly when we want the cached copy.
    const fetchServerVersion = async () => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);
        const res = await fetch(versionUrl, {
          headers: getAuthHeaders(),
          signal: controller.signal,
        });
        clearTimeout(timeout);
        if (!res.ok) return null;
        const json = await res.json();
        return json?.version || null;
      } catch {
        return null;
      }
    };

    // Download the full PDF from the server and return its bytes + version.
    const downloadFromServer = async () => {
      const res = await fetch(pdfUrl, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const version = res.headers.get('X-Doc-Version') || null;
      const data = await res.arrayBuffer();
      return { data, version };
    };

    const renderFromData = async (data) => {
      // pdf.js consumes (detaches) the buffer, so hand it a copy and keep ours.
      const loadingTask = pdfjsLib.getDocument({ data: data.slice(0) });
      const pdf = await loadingTask.promise;
      if (cancelled) return;
      setPdfDoc(pdf);
      setTotalPages(pdf.numPages);
      setIsLoading(false);
    };

    const loadPDF = async () => {
      setIsLoading(true);
      setError(null);
      setIsOfflineCopy(false);

      const cached = await getCachedDoc(filename);
      const serverVersion = await fetchServerVersion();

      // Offline (or version endpoint unreachable): fall back to any cached copy.
      if (!serverVersion) {
        if (cached?.data) {
          if (cancelled) return;
          setIsOfflineCopy(true);
          try {
            await renderFromData(cached.data);
          } catch (err) {
            if (cancelled) return;
            console.error('Error rendering cached Calor PDF:', err);
            setError(`Failed to load cached PDF: ${err.message}`);
            setIsLoading(false);
          }
          return;
        }
        if (cancelled) return;
        setError(
          'Unable to reach the server and no offline copy is saved yet. ' +
            'Connect to the internet once to download this document for offline use.',
        );
        setIsLoading(false);
        return;
      }

      // Cache hit with matching version: serve instantly from cache.
      if (cached?.data && cached.version && cached.version === serverVersion) {
        try {
          await renderFromData(cached.data);
          return;
        } catch (err) {
          console.warn('Cached copy failed to render, re-downloading:', err?.message);
        }
      }

      // No cache, or a newer version has been pushed: download and re-cache.
      try {
        const { data, version } = await downloadFromServer();
        if (cancelled) return;
        await renderFromData(data);
        await saveCachedDoc(filename, version || serverVersion, data);
      } catch (err) {
        if (cancelled) return;
        // Network died mid-flight but we still have an older cached copy.
        if (cached?.data) {
          setIsOfflineCopy(true);
          try {
            await renderFromData(cached.data);
            return;
          } catch (renderErr) {
            console.error('Error rendering fallback cached PDF:', renderErr);
          }
        }
        console.error('Error loading Calor PDF:', err);
        setError(`Failed to load PDF: ${err.message}`);
        setIsLoading(false);
      }
    };

    loadPDF();

    return () => {
      cancelled = true;
    };
  }, [pdfUrl, versionUrl, filename, authorised]);

  const highlightSearchResults = useCallback(
    async (pageNum, viewport, searchTerm) => {
      if (!highlightLayerRef.current || !searchTerm || searchResults.length === 0) {
        if (highlightLayerRef.current) {
          highlightLayerRef.current.innerHTML = '';
        }
        return;
      }

      highlightLayerRef.current.style.width = `${viewport.width}px`;
      highlightLayerRef.current.style.height = `${viewport.height}px`;
      highlightLayerRef.current.innerHTML = '';

      const pageResults = searchResults.filter((result) => result.page === pageNum);
      if (pageResults.length === 0) return;

      try {
        const page = await pdfDoc.getPage(pageNum);
        const textContent = await page.getTextContent();

        let fullText = '';
        const charPositions = [];

        textContent.items.forEach((item) => {
          fullText += item.str;
          for (let i = 0; i < item.str.length; i++) {
            charPositions.push({
              char: item.str[i],
              x: item.transform[4] + (i * item.width) / item.str.length,
              y: item.transform[5],
              width: item.width / item.str.length,
              height: item.height,
              transform: item.transform,
            });
          }
          fullText += ' ';
          charPositions.push({ char: ' ', x: 0, y: 0, width: 0, height: 0 });
        });

        const lowerSearchTerm = searchTerm.toLowerCase();
        const lowerText = fullText.toLowerCase();

        let index = 0;
        while ((index = lowerText.indexOf(lowerSearchTerm, index)) !== -1) {
          if (index < charPositions.length && index + lowerSearchTerm.length <= charPositions.length) {
            const firstChar = charPositions[index];
            const lastCharIndex = index + lowerSearchTerm.length - 1;
            if (lastCharIndex < charPositions.length) {
              const lastChar = charPositions[lastCharIndex];
              const highlight = document.createElement('div');
              highlight.style.position = 'absolute';
              highlight.style.left = `${firstChar.x}px`;
              highlight.style.top = `${viewport.height - firstChar.y - firstChar.height}px`;
              highlight.style.width = `${lastChar.x + lastChar.width - firstChar.x}px`;
              highlight.style.height = `${firstChar.height}px`;
              highlight.style.backgroundColor = 'rgba(0, 255, 0, 0.3)';
              highlight.style.pointerEvents = 'none';
              highlight.style.borderRadius = '2px';

              const isCurrentMatch =
                searchResults[currentSearchIndex]?.page === pageNum &&
                searchResults[currentSearchIndex]?.index === index;
              if (isCurrentMatch) {
                highlight.style.backgroundColor = 'rgba(255, 215, 0, 0.5)';
                highlight.style.border = '2px solid #FFD700';
              }

              highlightLayerRef.current.appendChild(highlight);
            }
          }
          index += lowerSearchTerm.length;
        }
      } catch (err) {
        console.error('Error highlighting search results:', err);
      }
    },
    [pdfDoc, searchResults, currentSearchIndex],
  );

  const renderPage = useCallback(
    async (pageNum, zoomLevel) => {
      if (!pdfDoc || !canvasRef.current) return;

      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }

      try {
        const page = await pdfDoc.getPage(pageNum);
        const pageRotation = page.rotate || 0;
        const viewport = page.getViewport({ scale: zoomLevel, rotation: pageRotation });

        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        context.setTransform(1, 0, 0, 1, 0, 0);
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = 'white';
        context.fillRect(0, 0, canvas.width, canvas.height);

        renderTaskRef.current = page.render({
          canvasContext: context,
          viewport,
        });
        await renderTaskRef.current.promise;
        renderTaskRef.current = null;

        highlightSearchResults(pageNum, viewport, activeSearchTermRef.current);

        if (textLayerRef.current) {
          textLayerRef.current.innerHTML = '';
          textLayerRef.current.style.width = `${viewport.width}px`;
          textLayerRef.current.style.height = `${viewport.height}px`;

          const textContent = await page.getTextContent();
          textContent.items.forEach((item) => {
            const div = document.createElement('div');
            div.textContent = item.str;
            div.style.position = 'absolute';
            div.style.left = `${item.transform[4]}px`;
            div.style.top = `${viewport.height - item.transform[5] - item.height}px`;
            div.style.fontSize = `${item.height}px`;
            div.style.fontFamily = item.fontName || 'sans-serif';
            div.style.color = 'transparent';
            div.style.userSelect = 'text';
            div.style.pointerEvents = 'auto';
            textLayerRef.current.appendChild(div);
          });
        }
      } catch (err) {
        renderTaskRef.current = null;
        if (err.name === 'RenderingCancelledException') return;
        console.error('Error rendering page:', err);
      }
    },
    [pdfDoc, highlightSearchResults],
  );

  useEffect(() => {
    if (pdfDoc && currentPage >= 1 && currentPage <= totalPages) {
      renderPage(currentPage, zoom);
    }
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
        renderTaskRef.current = null;
      }
    };
  }, [pdfDoc, currentPage, zoom, totalPages, renderPage]);

  useEffect(() => {
    if (pdfDoc && currentPage && searchResults.length > 0 && highlightLayerRef.current) {
      const canvas = canvasRef.current;
      if (canvas) {
        pdfDoc
          .getPage(currentPage)
          .then((page) => {
            const pageRotation = page.rotate || 0;
            const viewport = page.getViewport({ scale: zoom, rotation: pageRotation });
            highlightSearchResults(currentPage, viewport, activeSearchTermRef.current);
          })
          .catch((err) => console.error('Error re-highlighting:', err));
      }
    }
  }, [currentSearchIndex, pdfDoc, currentPage, zoom, searchResults, highlightSearchResults]);

  const getPageText = useCallback(
    async (pageNum) => {
      if (!pdfDoc) return '';
      if (pageTextCache.has(pageNum)) return pageTextCache.get(pageNum);

      try {
        const page = await pdfDoc.getPage(pageNum);
        const textContent = await page.getTextContent();
        const text = textContent.items.map((item) => item.str).join(' ');
        setPageTextCache((prev) => new Map(prev).set(pageNum, text));
        return text;
      } catch (err) {
        console.error('Error extracting text from page:', err);
        return '';
      }
    },
    [pdfDoc, pageTextCache],
  );

  const performSearch = useCallback(
    async (query) => {
      if (!query.trim() || !pdfDoc) {
        setSearchResults([]);
        setCurrentSearchIndex(0);
        return;
      }
      setIsSearching(true);
      const results = [];
      const searchTerm = query.toLowerCase();

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const pageText = await getPageText(pageNum);
        const lowerPageText = pageText.toLowerCase();
        let index = 0;
        while ((index = lowerPageText.indexOf(searchTerm, index)) !== -1) {
          results.push({
            page: pageNum,
            index,
            text: pageText.substring(Math.max(0, index - 20), index + searchTerm.length + 20),
          });
          index += searchTerm.length;
        }
      }

      setSearchResults(results);
      setCurrentSearchIndex(0);
      setIsSearching(false);
      activeSearchTermRef.current = query;

      if (results.length > 0) {
        setCurrentPage(results[0].page);
      }
    },
    [pdfDoc, totalPages, getPageText],
  );

  const handleSearchChange = (e) => setSearchQuery(e.target.value);
  const handleSearchSubmit = () => performSearch(searchQuery);

  const handleSearchNext = useCallback(() => {
    if (searchResults.length === 0) return;
    const nextIndex = (currentSearchIndex + 1) % searchResults.length;
    setCurrentSearchIndex(nextIndex);
    setCurrentPage(searchResults[nextIndex].page);
  }, [searchResults, currentSearchIndex]);

  const handleSearchPrev = useCallback(() => {
    if (searchResults.length === 0) return;
    const prevIndex =
      currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    setCurrentSearchIndex(prevIndex);
    setCurrentPage(searchResults[prevIndex].page);
  }, [searchResults, currentSearchIndex]);

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setCurrentSearchIndex(0);
    activeSearchTermRef.current = '';
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '/' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        searchInputRef.current?.focus();
        return;
      }
      if (document.activeElement === searchInputRef.current) {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (e.shiftKey) handleSearchPrev();
          else handleSearchNext();
        }
        return;
      }
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          setCurrentPage((prev) => Math.max(1, prev - 1));
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          setCurrentPage((prev) => Math.min(totalPages, prev + 1));
          break;
        case '=':
        case '+':
          e.preventDefault();
          setZoom((prev) => Math.min(3.0, prev + 0.25));
          break;
        case '-':
          e.preventDefault();
          setZoom((prev) => Math.max(0.5, prev - 0.25));
          break;
        default:
          break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [totalPages, handleSearchNext, handleSearchPrev]);

  const handleZoomIn = () => setZoom((prev) => Math.min(3.0, prev + 0.25));
  const handleZoomOut = () => setZoom((prev) => Math.max(0.5, prev - 0.25));
  const handleFitToWidth = () => {
    if (containerRef.current && canvasRef.current) {
      const containerWidth = containerRef.current.clientWidth - 40;
      const canvasWidth = canvasRef.current.width / zoom;
      const newZoom = containerWidth / canvasWidth;
      setZoom(Math.min(3.0, Math.max(0.5, newZoom)));
    }
  };

  const handlePageChange = (e) => {
    const page = parseInt(e.target.value, 10);
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };
  const handlePreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  if (!authorised) return null;

  return (
    <PVSPageLayout className="flex flex-col">
      <div className="pvs-glass border-b border-pvs-border shadow-pvs-glow px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-3 py-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200"
              title="Back"
              type="button"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </button>
            {onReturnHome && (
              <button
                onClick={onReturnHome}
                className="flex items-center gap-2 px-3 py-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200"
                title="Home"
                type="button"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">Home</span>
              </button>
            )}
          </div>

          <div className="flex-1 text-center">
            <h1 className="text-lg font-semibold text-white truncate">{title}</h1>
            {isOfflineCopy && (
              <span className="text-xs text-amber-300">Offline copy — showing last saved version</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-pvs-text-dim" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSearchSubmit();
                      }
                    }}
                    placeholder="Search in document..."
                    className="pl-9 pr-8 py-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50 placeholder-pvs-text-dim w-48"
                  />
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-pvs-text-dim hover:text-pvs-text-muted"
                      type="button"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <button
                  onClick={handleSearchSubmit}
                  disabled={!searchQuery.trim()}
                  className="px-3 py-2 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed text-sm"
                  title="Search (Enter)"
                  type="button"
                >
                  Search
                </button>

                {searchResults.length > 0 && (
                  <div className="flex items-center gap-1 ml-2">
                    <span className="text-sm text-pvs-text-muted">
                      {currentSearchIndex + 1} of {searchResults.length}
                    </span>
                    <button
                      onClick={handleSearchPrev}
                      className="p-1 text-pvs-text-muted hover:text-white rounded"
                      title="Previous match (Shift+Enter)"
                      type="button"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={handleSearchNext}
                      className="p-1 text-pvs-text-muted hover:text-white rounded"
                      title="Next match (Enter)"
                      type="button"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {isSearching && (
                  <div className="ml-2 text-sm text-pvs-text-muted">Searching...</div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 border-l border-pvs-border pl-2">
              <button
                onClick={handleZoomOut}
                className="p-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200"
                title="Zoom Out (-)"
                type="button"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-sm text-pvs-text-muted min-w-[4rem] text-center">
                {Math.round(zoom * 100)}%
              </span>
              <button
                onClick={handleZoomIn}
                className="p-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200"
                title="Zoom In (+)"
                type="button"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                onClick={handleFitToWidth}
                className="p-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200"
                title="Fit to Width"
                type="button"
              >
                <Maximize className="w-4 h-4" />
              </button>
            </div>

            {totalPages > 0 && (
              <div className="flex items-center gap-2 border-l border-pvs-border pl-2">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage <= 1}
                  className="p-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ touchAction: 'manipulation' }}
                  title="Previous Page (← or ↑)"
                  type="button"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm text-pvs-text-muted">Page</span>
                <input
                  type="number"
                  min="1"
                  max={totalPages}
                  value={currentPage}
                  onChange={handlePageChange}
                  className="w-16 px-2 py-1 bg-pvs-input-bg border border-pvs-input-border text-white rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50"
                />
                <span className="text-sm text-pvs-text-muted">of {totalPages}</span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage >= totalPages}
                  className="p-2 text-pvs-text-muted hover:bg-pvs-card-light rounded-lg transition-colors duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                  style={{ touchAction: 'manipulation' }}
                  title="Next Page (→ or ↓)"
                  type="button"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-pvs-bg" ref={containerRef}>
        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pvs-accent mx-auto mb-4" />
              <p className="text-pvs-text-muted">Loading PDF...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-96">
            <div className="text-center max-w-md mx-auto p-6">
              <p className="text-red-400 mb-2 font-medium">{error}</p>
              <p className="text-pvs-text-muted text-sm mb-4">
                Make sure <code className="text-pvs-accent">{filename}</code> is installed on the
                server in <code className="text-pvs-accent">backend/assets/calor/documents/</code>.
              </p>
              <button
                onClick={onBack}
                className="flex items-center gap-2 px-4 py-2 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-all duration-200 mx-auto"
                type="button"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center py-8 gap-6">
            <div className="relative bg-white shadow-lg" style={{ zIndex: 1 }}>
              <canvas ref={canvasRef} className="block" style={{ maxWidth: '100%', height: 'auto' }} />
              <div
                ref={textLayerRef}
                className="absolute top-0 left-0 overflow-hidden leading-none"
                style={{ mixBlendMode: 'multiply', pointerEvents: 'none' }}
              />
              <div
                ref={highlightLayerRef}
                className="absolute top-0 left-0 overflow-hidden leading-none pointer-events-none"
              />
            </div>

            <div
              className="flex items-center gap-4 pvs-glass border border-pvs-border px-6 py-4 rounded-lg shadow-pvs-glow"
              style={{ position: 'relative', zIndex: 10, marginTop: '20px' }}
            >
              <button
                onClick={handlePreviousPage}
                disabled={currentPage <= 1}
                className="flex items-center gap-2 px-6 py-3 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ touchAction: 'manipulation', minHeight: '44px', minWidth: '44px' }}
                title="Previous Page"
                type="button"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Previous</span>
              </button>

              <div className="text-center px-4">
                <div className="text-lg font-semibold text-white">
                  Page {currentPage} of {totalPages}
                </div>
              </div>

              <button
                onClick={handleNextPage}
                disabled={currentPage >= totalPages}
                className="flex items-center gap-2 px-6 py-3 bg-pvs-accent text-pvs-bg font-semibold rounded-lg hover:bg-pvs-accent-dim transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ touchAction: 'manipulation', minHeight: '44px', minWidth: '44px' }}
                title="Next Page"
                type="button"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </PVSPageLayout>
  );
};

export default CalorPDFViewer;
